import { useEffect, useState, useCallback } from "react";
import { api, apiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { DownloadSimple, CheckCircle, Money, WarningCircle } from "@phosphor-icons/react";

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);

const PERIODS = [
  { key: "7", label: "Last 7 days" },
  { key: "14", label: "Last 2 weeks (biweekly)" },
  { key: "15", label: "Last 15 days (semi-monthly)" },
  { key: "30", label: "Last 30 days (monthly)" },
];

export default function Payroll() {
  const [period, setPeriod] = useState("14");
  const [range, setRange] = useState({ start: daysAgo(14), end: today() });
  const [data, setData] = useState(null);
  const [cards, setCards] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [p, t] = await Promise.all([
      api.get("/payroll", { params: { start: range.start, end: range.end } }),
      api.get("/timecards", { params: { start: range.start, end: range.end } }),
    ]);
    setData(p.data);
    setCards(t.data.filter((c) => c.clock_out));
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const setPreset = (k) => {
    setPeriod(k);
    setRange({ start: daysAgo(Number(k)), end: today() });
  };

  const approveAll = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/timecards/approve-range", { start: range.start, end: range.end });
      toast.success(`${r.approved} timecard(s) approved`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
    setBusy(false);
  };

  const toggle = async (id) => {
    try {
      await api.patch(`/timecards/${id}/approve`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const download = async () => {
    setBusy(true);
    try {
      const { data: blob } = await api.get(
        `/payroll?start=${range.start}&end=${range.end}&format=csv`, { responseType: "blob" });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `payroll_${range.start}_to_${range.end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast.success("Payroll file downloaded");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div data-testid="payroll-page">
      <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Payroll</p>
      <h1 className="mt-3 text-4xl font-bold tracking-tight">Pay period export</h1>
      <p className="mt-3 text-muted-foreground max-w-2xl">
        Approve the hours your team worked, then download a file your bookkeeper can drop straight into
        payroll. Overtime is calculated per week over 40 hours at 1.5×.
      </p>

      <div className="mt-8 flex flex-wrap gap-4 items-end">
        <div className="w-64">
          <Label className="text-sm font-bold">Pay period</Label>
          <Select value={period} onValueChange={setPreset}>
            <SelectTrigger className="mt-2 h-11 rounded-xl bg-white" data-testid="payroll-period"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm font-bold">From</Label>
          <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })}
                 className="mt-2 h-11 rounded-xl w-44" data-testid="payroll-start" />
        </div>
        <div>
          <Label className="text-sm font-bold">To</Label>
          <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })}
                 className="mt-2 h-11 rounded-xl w-44" data-testid="payroll-end" />
        </div>
        <Button variant="outline" onClick={approveAll} disabled={busy}
                className="h-11 px-6 rounded-full font-bold" data-testid="approve-all-btn">
          <CheckCircle size={17} className="mr-2" /> Approve all hours
        </Button>
        <Button onClick={download} disabled={busy}
                className="h-11 px-6 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                data-testid="payroll-download-btn">
          <DownloadSimple size={17} className="mr-2" /> Download payroll file
        </Button>
      </div>

      {!data ? (
        <p className="mt-10 text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="mt-8 grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {[["Gross payroll", money(data.gross_payroll), "text-[#10B981]", "payroll-gross"],
              ["Total hours", `${data.total_hours}h`, "", "payroll-hours"],
              ["Overtime hours", `${data.total_overtime_hours}h`, "text-[#EF4444]", "payroll-ot"],
              ["Awaiting approval", data.unapproved_timecards, "text-[#F59E0B]", "payroll-pending"]].map(
              ([k, v, c, tid]) => (
                <div key={k} className="bg-white rounded-2xl border border-border p-6" data-testid={tid}>
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{k}</p>
                  <p className={`mt-3 font-display text-3xl font-bold ${c}`}>{v}</p>
                </div>
              ))}
          </div>

          {data.unapproved_timecards > 0 && (
            <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 p-5 flex items-center gap-4"
                 data-testid="payroll-warning">
              <WarningCircle size={26} weight="fill" className="text-[#F59E0B] shrink-0" />
              <p className="text-sm font-semibold">
                {data.unapproved_timecards} timecard(s) in this period are not approved yet, so their hours
                are excluded from the file above.
              </p>
            </div>
          )}

          <h2 className="mt-10 font-display text-2xl font-semibold">Pay summary</h2>
          <div className="mt-4 bg-white rounded-2xl border border-border overflow-x-auto">
            {data.rows.length === 0 ? (
              <p className="p-8 text-muted-foreground" data-testid="payroll-empty">
                No approved hours in this period yet.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    {["Staff", "Regular", "Overtime", "Total", "Wage", "Regular pay", "OT pay", "Gross"].map((h) => (
                      <th key={h} className="text-left p-4 text-xs font-bold uppercase tracking-[0.1em] whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.email} className="border-b border-border last:border-0"
                        data-testid={`payroll-row-${r.email}`}>
                      <td className="p-4 font-bold whitespace-nowrap">{r.staff}</td>
                      <td className="p-4">{r.regular_hours}h</td>
                      <td className="p-4">{r.overtime_hours}h</td>
                      <td className="p-4">{r.total_hours}h</td>
                      <td className="p-4">{money(r.hourly_wage)}</td>
                      <td className="p-4">{money(r.regular_pay)}</td>
                      <td className="p-4">{money(r.overtime_pay)}</td>
                      <td className="p-4 font-bold whitespace-nowrap">{money(r.gross_pay)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <h2 className="mt-10 font-display text-2xl font-semibold">Timecards to approve</h2>
          <div className="mt-4 space-y-3" data-testid="approval-list">
            {cards.length === 0 && <p className="text-muted-foreground">No completed timecards in this period.</p>}
            {cards.map((c) => (
              <div key={c.id} className="bg-white rounded-2xl border border-border p-5 flex flex-wrap gap-4 items-center"
                   data-testid={`approve-row-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-bold">{c.staff_name || c.staff_email}</p>
                  <p className="text-xs text-muted-foreground mt-1 font-semibold">
                    {c.date}{c.room ? ` · ${c.room}` : ""}{c.approved_by ? ` · approved by ${c.approved_by}` : ""}
                  </p>
                </div>
                <p className="font-display text-lg font-bold">{c.hours || 0}h</p>
                <Button size="sm" onClick={() => toggle(c.id)}
                        className={`rounded-full font-bold ${c.approved
                          ? "bg-[#10B981] hover:bg-emerald-600"
                          : "bg-muted text-foreground hover:bg-slate-200"}`}
                        data-testid={`toggle-approve-${c.id}`}>
                  <CheckCircle size={16} className="mr-1" /> {c.approved ? "Approved" : "Approve"}
                </Button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
